import '../../base/outline-container/outline-container';
import '../ghs-button/ghs-button';
import '../ghs-logo/ghs-logo';
import '../ghs-icon/ghs-icon';
import { OutlineElement } from '../../base/outline-element/outline-element';
import {
  CSSResultGroup,
  html,
  nothing,
  PropertyValues,
  TemplateResult,
} from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import componentStyles from './ghs-header.css.lit';

import { LogoVariantType } from '../ghs-logo/ghs-logo';

import {
  screenSizeMap,
  ScreenSizeController,
} from '../../controllers/screen-size-controller';

export type VisibilityStatusState =
  | 'collapsed'
  | 'search-expanded'
  | 'hamburger-expanded'
  | string;

/**
 * The Header component
 *
 * @element ghs-header
 */
@customElement('ghs-header')
export class GhsHeader extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  // eslint-disable-next-line
  // @ts-ignore
  private _screenSizeController = new ScreenSizeController(this);

  /**
   * The logo variant
   * @param logoVariant
   * @default ghsLogoMain
   */
  @property({ type: String, reflect: true, attribute: 'logo-variant' })
  logoVariant: LogoVariantType = 'ghsLogoMain';

  @property({ type: Boolean, reflect: false, attribute: 'mychart-enabled' })
  myChartEnabled = false;

  /**
   * The search type variant.
   * @param visibilityStatusState
   * @default 'collapsed'
   */
  @property({ reflect: true, attribute: 'visibility-status' })
  visibilityStatusState: VisibilityStatusState = 'collapsed';

  @property({ type: String, attribute: 'mychart-link' })
  mychartLink: String = '';

  @state()
  uniqueId = Math.floor(Math.random() * 10000).toString();

  @state()
  isOpen: 'true' | 'false' =
    this.visibilityStatusState === 'collapsed' ? 'false' : 'true';

  overlay: HTMLDivElement;

  @query('#search-mobile')
  searchMobileButton: HTMLElement;

  @query('#search-desktop')
  searchDesktopButton: HTMLElement;

  // Function to handle the menu expansion state.
  expansionStateFunction = (caller: String) => (event: Event | undefined) => {
    // Ignore all keydown events other than the Esc key.
    if (
      event instanceof KeyboardEvent &&
      event.type === 'keydown' &&
      event.key !== 'Escape'
    ) {
      return;
    }

    // Unbind keydown event if search is currently expanded.
    if (this.visibilityStatusState === 'search-expanded') {
      document.removeEventListener('keydown', this.expandSearchFunction);
    }

    const callerConcatd = caller + '-expanded';

    if (this.visibilityStatusState !== callerConcatd) {
      this.visibilityStatusState = callerConcatd;
      // Focus search input text element and bind Esc key to close the modal.
      if (caller === 'search') {
        this.focusSearchTextInput();
        document.addEventListener('keydown', this.expandSearchFunction);
      }
    } else {
      this.visibilityStatusState = 'collapsed';
      // Reset input focus to the original search button when closing.
      if (caller === 'search') {
        this.focusSearchActivateButton();
      }
    }
  };

  // Instance of search function to add/remove the event listener.
  expandSearchFunction = this.expansionStateFunction('search');

  focusSearchTextInput(): void {
    const headerSearchBarSlot = this.shadowRoot?.querySelector(
      'slot[name="header-search-bar"]'
    ) as HTMLSlotElement;

    const textInput = headerSearchBarSlot
      ?.assignedElements()?.[0]
      ?.querySelector('ghs-search-bar input[type="text"]') as HTMLElement;
    setTimeout(() => textInput?.focus());
  }

  focusSearchActivateButton(): void {
    const isMobile: boolean =
      window.innerWidth < (screenSizeMap.get('lg') as number);
    const searchButton = isMobile
      ? this.searchMobileButton
      : this.searchDesktopButton;
    setTimeout(() => searchButton.focus());
  }

  headerUtilityMenuTemplate(isBH: boolean): TemplateResult {
    return html`
      <div class="header-utilities-section desktop">
        <div class="header-utilities-link-slot-wrapper">
          <slot name="header-utilities-link-ul"></slot>
        </div>
        ${isBH ? this.bhHeaderSearchControls() : this.ghsHeaderSearchControls()}
      </div>
    `;
  }

  ghsHeaderSearchControls(): TemplateResult {
    return html` <button
      id="search-desktop"
      aria-label="Toggle site search"
      class="desktop-search-control"
      aria-expanded="${this.isOpen}"
      aria-controls="${this.uniqueId}-search-dropdown"
      @click=${this.expandSearchFunction}
    >
      <span class="label">Search Gundersen</span>
      <ghs-icon name="search" size="18px" site=${this.site}></ghs-icon>
      <ghs-icon name="close" size="20px" site=${this.site}></ghs-icon>
    </button>`;
  }

  bhHeaderSearchControls(): TemplateResult {
    return html` <button
      id="search-desktop"
      aria-label="Toggle site search"
      class="desktop-search-control"
      aria-expanded="${this.isOpen}"
      aria-controls="${this.uniqueId}-search-dropdown"
      @click=${this.expandSearchFunction}
    >
      <ghs-icon name="search" size="18px" site=${this.site}></ghs-icon>
      <span class="label">Search</span>
      <ghs-icon name="close" size="16px" site=${this.site}></ghs-icon>
    </button>`;
  }

  // Render header mobile controls.
  headerMobileControls(isMobilePlus: Boolean): TemplateResult {
    return html`
      <div class="header-mobile-controls-organizer">
        <a class="header-logo-wrapper" href="/">
          <span class="visually-hidden">Home</span>
          <ghs-logo
            logo-variant="${this.logoVariant}"
            site=${this.site}
          ></ghs-logo>
        </a>
        ${!isMobilePlus ? this.headerNewcoButtonSlot() : nothing}
        <div class="header-mobile-controls-section">
          <button
            id="search-mobile"
            aria-label="Toggle site search"
            aria-expanded="${this.isOpen}"
            aria-controls="${this.uniqueId}-search-dropdown"
            class="header-mobile-controls-search-wrapper header-controls-trigger ${this
              .visibilityStatusState}"
            @click=${this.expandSearchFunction}
          >
            ${this.visibilityStatusState === 'search-expanded'
              ? html`<ghs-icon
                  name="close"
                  size="16px"
                  mobile-size="16px"
                  site=${this.site}
                ></ghs-icon>`
              : html`<ghs-icon
                    name="search"
                    size="16px"
                    mobile-size="16px"
                    site=${this.site}
                  ></ghs-icon>
                  <span class="label">Search</span> `}
          </button>
          <button
            id="hamburger"
            aria-label="Toggle main menu"
            aria-expanded="${this.isOpen}"
            aria-controls="${this.uniqueId}-menu-dropdown"
            class="header-mobile-controls-hamburger-wrapper header-controls-trigger ${this
              .visibilityStatusState}"
            @click=${this.expansionStateFunction('hamburger')}
          >
            ${(this,
            this.visibilityStatusState === 'hamburger-expanded'
              ? html`
                  <ghs-icon
                    name="close"
                    mobile-size="16px"
                    size="16px"
                    site=${this.site}
                  ></ghs-icon>
                `
              : html`
                  <ghs-icon
                    name="hamburger"
                    mobile-size="28px"
                    size="28px"
                    site=${this.site}
                  ></ghs-icon>
                  <span class="label">Menu</span>
                `)}
          </button>
        </div>
      </div>

      <!-- Uncomment only for the static mobile menu. -->
      <!-- <input class="hamburger-check" type="checkbox" /> -->
    `;
  }

  headerSearchSection(): TemplateResult {
    return html`<div
      class="header-search-wrapper"
      id="${this.uniqueId}-search-dropdown"
    >
      <slot name="header-search-bar"></slot>
    </div>`;
  }

  ghsHeaderDropdownMenuSection(isMobile: boolean): TemplateResult {
    return html` <div
      class="header-dropdown-menu-section"
      id="${this.uniqueId}-menu-dropdown"
    >
      ${isMobile
        ? html`
            <div class="header-dropdown-buttons">
              <slot name="header-dropdown-buttons"></slot>
            </div>
          `
        : nothing}
      <slot name="header-dropdown-menu-items"></slot>

      ${isMobile
        ? html`<slot name="header-utilities-link-ul"></slot>`
        : nothing}
      <div class="header-dropdown-actions-section">
        ${!isMobile
          ? html`<slot name="header-dropdown-action-items"></slot>`
          : nothing}
        ${!isMobile ? this.headerMyChartButtonSlot() : nothing}
      </div>
    </div>`;
  }

  headerMobileTopLinks(): TemplateResult {
    return html`<div
      class="header-mobile-top-links ${this.visibilityStatusState}"
    >
      <outline-container x-padding>
        <div class="header-utilities-section mobile">
          ${this.myChartEnabled
            ? html`<slot name="mobile-header-mychart-link-dropdown"></slot>`
            : nothing}
          <div class="header-utilities-link-slot-wrapper">
            <slot name="header-mobile-top-links"></slot>
          </div>
          ${!this.myChartEnabled && !!this.mychartLink
            ? html`<slot name="mobile-header-mychart-link"></slot>`
            : nothing}
        </div>
      </outline-container>
    </div>`;
  }

  // This uses a switch because there is a possibility/probability a third case will be added here. (it was already, then was reverted for MVP)
  bhHeaderDropdownMenuSection(isMobile: boolean): TemplateResult {
    switch (true) {
      case isMobile: {
        return html` <div
          class="header-dropdown-menu-section"
          id="${this.uniqueId}-menu-dropdown"
        >
          ${this.headerMyChartButtonSlot()}
          <slot name="header-dropdown-menu-items"></slot>
        </div>`;
      }
      default: {
        return html` <div
          class="header-dropdown-menu-section"
          id="${this.uniqueId}-menu-dropdown"
        >
          <slot name="header-dropdown-menu-items"></slot>
          ${this.headerMyChartButtonSlot()}
        </div>`;
      }
    }
  }

  headerMyChartButtonSlot(): TemplateResult {
    return html`<div class="header-button-wrapper">
      <slot name="header-button"></slot>
    </div>`;
  }

  headerNewcoButtonSlot(): TemplateResult {
    return html`<slot name="newco-button"></slot>`;
  }

  firstUpdated() {
    super.firstUpdated();
    if (this.site !== 'BH') {
      return;
    }
    const body = this.closestElement('body');
    this.overlay = document.createElement('div');
    this.overlay.setAttribute('class', 'overlay');
    this.overlay.style.position = 'fixed';
    this.overlay.style.inset = '0';
    this.overlay.style.background = 'rgba(35, 35, 35, 0.70)';
    this.overlay.style.zIndex = '40';
    this.overlay.style.display = 'none';
    this.overlay.addEventListener('click', this.expandSearchFunction);
    body?.append(this.overlay);
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (this.site !== 'BH') {
      return;
    }
    if (this.visibilityStatusState === 'search-expanded') {
      this.overlay.style.display = 'block';
    } else {
      this.overlay.style.display = 'none';
    }
  }

  render(): TemplateResult {
    const isMobile: boolean =
      window.innerWidth < (screenSizeMap.get('lg') as number);
    const isMobilePlus: boolean =
      window.innerWidth < (screenSizeMap.get('xl') as number);
    const isBH: boolean = this.site === 'BH';

    return html`
      ${isMobilePlus ? this.headerNewcoButtonSlot() : nothing}
      ${isMobile ? this.headerMobileTopLinks() : nothing}
      <header class="header-wrapper">
        <outline-container x-padding class="${this.visibilityStatusState}">
          <div class="header">
            ${this.headerMobileControls(isMobilePlus)}
            <hr />
            <div class="header-links-master-section">
              <div class="header-desktop-search-toggle-section">
                ${this.headerSearchSection()}
                ${!isMobile ? this.headerUtilityMenuTemplate(isBH) : nothing}
              </div>
            </div>
          </div>

          ${isBH
            ? this.bhHeaderDropdownMenuSection(isMobile)
            : this.ghsHeaderDropdownMenuSection(isMobile)}
          ${isMobile && isBH ? this.headerUtilityMenuTemplate(isBH) : nothing}
        </outline-container>
      </header>
    `;
  }
}
